import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { mq } from '../../../../common/src/utils/styledComponentsUtils';

// Components
import LargeButton from '../../../../common/src/components/LargeButton';
import RadioButton from '../../../../flows/src/components/ui/advisory-flow/components/shared-components/RadioButton';
import CancelButton from './CancelButton';
import { LouiseQuote } from '../../../../../types/Quotes';
import { getGuaranteeNameForKey } from '../../services/productFactoryService';

interface Props {
  quote: LouiseQuote;
  onCancel: () => void;
  onContinue: () => void;
}

const OfferConfirmation: React.FC<React.PropsWithChildren<Props>> = ({
  quote,
  onCancel,
  onContinue,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<boolean>();

  return (
    <div>
      <Title
        dangerouslySetInnerHTML={{
          __html: t('Add x to your selected offers?', {
            type: `${
              quote.info.company?.name || t('Insurer')
            }: ${getGuaranteeNameForKey(
              quote.info.insurance_name,
              quote.guarantee_content,
            )}`,
          }),
        }}
      />
      <ConfirmationWrapper>
        <ConfirmationText>
          {t(
            'I confirm that I have not had any insurance claims related to x in the last five years',
            { type: t(`the_riskobject.${quote.info.insurance_type}`) },
          )}
        </ConfirmationText>
        <RadioButtons data-test-id="ADVISORY-CONVO_OFFERS_CONFIRMATION-MODAL_RADIO-BUTTONS">
          <StyledRadioButton
            isSelected={selected === false}
            onClick={() => setSelected(false)}
          >
            {t('No')}
          </StyledRadioButton>
          <RadioButton
            isSelected={selected === true}
            onClick={() => setSelected(true)}
          >
            {t('Yes')}
          </RadioButton>
        </RadioButtons>
      </ConfirmationWrapper>
      <Buttons>
        <LargeButton
          onClick={onContinue}
          disabled={selected !== true}
          dataTestId="ADVISORY-CONVO_OFFERS_CONFIRMATION-MODAL_CONTINUE-BUTTON"
        >
          {t('Continue')}
        </LargeButton>
        <StyledCancelButton onClick={onCancel} />
      </Buttons>
    </div>
  );
};

const StyledCancelButton = styled(CancelButton)`
  ${mq.laptop} {
    margin-top: 0;
    margin-right: 1.6rem;
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;

  ${mq.laptop} {
    flex-direction: row-reverse;
  }
`;

const StyledRadioButton = styled(RadioButton)`
  margin-right: 1.6rem;
`;

const ConfirmationText = styled.p`
  margin-bottom: 1.2rem;
`;

const RadioButtons = styled.div`
  display: flex;

  ${mq.laptop} {
    margin-left: 3.2rem;
  }
`;

const ConfirmationWrapper = styled.div`
  font-size: 1.4rem;
  padding: 1.6rem;
  margin-bottom: 2.4rem;
  border-radius: 6px;
  background-color: ${({ theme }) => theme.gray[50]};

  ${mq.laptop} {
    display: flex;
  }
`;

const Title = styled.h1`
  font-weight: 500;
  font-size: 1.8rem;
  margin-bottom: 1.6rem;

  > span {
    color: ${({ theme }) => theme.primary[500]};
  }
`;

export default OfferConfirmation;
